import {
  addDays,
  differenceInCalendarDays,
  differenceInMilliseconds,
  endOfDay,
  isAfter,
  isBefore,
  startOfDay,
} from 'date-fns';

import {DAYS_IN_WEEK} from '../constants/daysInWeek';
import type {BigCalendarEvent} from '../types/BigCalendarEvent';
import type {EventSegment} from '../types/EventSegment';
import {isMultiDayEvent} from './isMultiDayEvent';

const LAST_COLUMN = DAYS_IN_WEEK - 1;

/**
 * Sort order for packing: multi-day events first so long bars sit on top, then earlier start,
 * then longer duration, so two bars starting on the same day stack longest-first.
 */
const compareForPacking = (first: BigCalendarEvent, second: BigCalendarEvent) => {
  const multiDayDifference =
    Number(isMultiDayEvent(second.start, second.end)) -
    Number(isMultiDayEvent(first.start, first.end));
  if (multiDayDifference !== 0) {
    return multiDayDifference;
  }

  const startDifference = differenceInMilliseconds(first.start, second.start);
  if (startDifference !== 0) {
    return startDifference;
  }

  return (
    differenceInMilliseconds(second.end, second.start) -
    differenceInMilliseconds(first.end, first.start)
  );
};

/**
 * Lays out the events of one week row: a month-view week or the week view's all-day header. Each
 * event overlapping the week becomes one segment clipped to the week, and segments are packed
 * greedily into slots: a segment takes the lowest slot that is free on every column it covers, so
 * a bar keeps one row across all its days.
 *
 * Column and clipping use calendar days, so an all-day event normalised to midnight boundaries
 * covers exactly the days the organiser picked.
 *
 * Segments come back ordered by slot, then column, so rendering order is stable.
 */
export function layoutEventSegments(events: BigCalendarEvent[], weekStart: Date): EventSegment[] {
  const weekStartBoundary = startOfDay(weekStart);
  const weekEndBoundary = endOfDay(addDays(weekStartBoundary, LAST_COLUMN));

  const weekEvents = events
    .filter((event) => event.start <= weekEndBoundary && event.end >= weekStartBoundary)
    .sort(compareForPacking);

  const occupiedColumnsBySlot: boolean[][] = [];

  return weekEvents
    .map((event) => {
      const isStartClipped = isBefore(event.start, weekStartBoundary);
      const isEndClipped = isAfter(event.end, weekEndBoundary);

      const columnStart = isStartClipped
        ? 0
        : differenceInCalendarDays(event.start, weekStartBoundary);
      const columnEnd = isEndClipped
        ? LAST_COLUMN
        : differenceInCalendarDays(event.end, weekStartBoundary);
      const columnSpan = columnEnd - columnStart + 1;

      const slot = findFreeSlot(occupiedColumnsBySlot, columnStart, columnSpan);
      occupySlot(occupiedColumnsBySlot, slot, columnStart, columnSpan);

      return {event, slot, columnStart, columnSpan, isStartClipped, isEndClipped};
    })
    .sort((first, second) => first.slot - second.slot || first.columnStart - second.columnStart);
}

const findFreeSlot = (
  occupiedColumnsBySlot: boolean[][],
  columnStart: number,
  columnSpan: number
) => {
  const columns = Array.from({length: columnSpan}, (_, offset) => columnStart + offset);

  const freeSlot = occupiedColumnsBySlot.findIndex(
    (occupiedColumns) => !columns.some((column) => occupiedColumns[column])
  );

  return freeSlot === -1 ? occupiedColumnsBySlot.length : freeSlot;
};

const occupySlot = (
  occupiedColumnsBySlot: boolean[][],
  slot: number,
  columnStart: number,
  columnSpan: number
) => {
  const occupiedColumns = occupiedColumnsBySlot[slot] ?? Array(DAYS_IN_WEEK).fill(false);
  occupiedColumnsBySlot[slot] = occupiedColumns;

  for (let column = columnStart; column < columnStart + columnSpan; column++) {
    occupiedColumns[column] = true;
  }
};
